import { Box, Chip, ChipProps, SvgIconTypeMap, Tooltip, Typography, useTheme } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import React from 'react';

interface Props {
  label: string;
  color: ChipProps['color'];
  icon: OverridableComponent<SvgIconTypeMap>;
  iconOnly?: boolean;
  tooltip?: string;
  /** Only the chip: no tooltip, no full-width wrapper, no collapse to an icon. */
  chipOnly?: boolean;
}

const StatusChip: React.FC<Props> = ({ label, color, icon: Icon, iconOnly, tooltip, chipOnly }) => {
  const theme = useTheme();

  const paletteColor = color && color !== 'default' ? theme.palette[color] : undefined;
  const iconColor = paletteColor?.main;
  const labelColor = paletteColor?.light;

  const collapsedStyles = {
    '& .MuiChip-label': { display: 'none', padding: 0, color: labelColor },
    '& .MuiChip-icon': { margin: 0 },
    borderRadius: '50%',
    padding: '3px'
  };

  const chip = (
    <Chip
      variant="filled"
      size="small"
      color={color}
      icon={<Icon sx={{ fill: iconColor }} />}
      label={<Typography variant="caption">{label}</Typography>}
      sx={
        iconOnly
          ? collapsedStyles
          : {
              '& .MuiChip-label': { color: labelColor },
              [theme.containerQueries.down(80)]: collapsedStyles
            }
      }
    />
  );

  if (chipOnly) return chip;

  return (
    <Box
      sx={{
        containerType: 'inline-size',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: iconOnly ? 'center' : 'flex-start',
        alignItems: 'center'
      }}
    >
      <Tooltip
        title={
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {tooltip ?? label}
          </Typography>
        }
      >
        {chip}
      </Tooltip>
    </Box>
  );
};

export default StatusChip;
